import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ConfiguratorDatastoreService, DatastoreDto } from '@tj/tj-dmp-lib-api';
import { TranslateService } from '@ngx-translate/core';

import { ProfileService } from '../../../legacy/shared-libs/service/profile.service';
import { TransferService } from './transfer.service';
import { ConflictStrategy, ImportReport, TRANSFERABLE_TYPES, TransferableType, TransferObjectRef } from './transfer.model';

type CartLine = TransferObjectRef;

@Component({
    selector: 'dmp-tenant-transfer',
    templateUrl: './tenant-transfer.component.html',
    styleUrls: ['./tenant-transfer.component.scss'],
    standalone: false
})
export class TenantTransferComponent implements OnInit {
    breadcrumb = [{ label: this.translate.instant('menu.transfer.label') }];
    description = this.translate.instant('tenantTransfer.main.pageDescription');

    readonly types: TransferableType[] = [...TRANSFERABLE_TYPES];
    conflictStrategies: { label: string; value: ConflictStrategy }[] = [
        { label: this.translate.instant('tenantTransfer.main.strategyIgnore'), value: 'IGNORE' },
        { label: this.translate.instant('tenantTransfer.main.strategyOverwrite'), value: 'OVERWRITE' },
        { label: this.translate.instant('tenantTransfer.main.strategyDuplicate'), value: 'DUPLICATE' }
    ];

    allDatastores: DatastoreDto[] = [];
    sourceTenant: string;
    targetTenant: string;
    conflictStrategy: ConflictStrategy = 'IGNORE';

    cart: CartLine[] = [];

    report: ImportReport = null;
    reportIsDryRun = true;
    working = false;

    /** Signature the last dry-run report was computed against; the real transfer stays gated
     *  behind a dry-run whose signature still matches the current cart/target/strategy. */
    private lastDryRunSignature: string = null;

    constructor(
        private profileService: ProfileService,
        private datastoreService: ConfiguratorDatastoreService,
        private transferService: TransferService,
        private messageService: MessageService,
        private readonly translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedOptions());
    }

    private rebuildTranslatedOptions(): void {
        this.breadcrumb = [{ label: this.translate.instant('menu.transfer.label') }];
        this.description = this.translate.instant('tenantTransfer.main.pageDescription');
        this.conflictStrategies = [
            { label: this.translate.instant('tenantTransfer.main.strategyIgnore'), value: 'IGNORE' },
            { label: this.translate.instant('tenantTransfer.main.strategyOverwrite'), value: 'OVERWRITE' },
            { label: this.translate.instant('tenantTransfer.main.strategyDuplicate'), value: 'DUPLICATE' }
        ];
    }

    typeLabel(type: TransferableType): string {
        return this.translate.instant('tenantTransfer.types.' + type);
    }

    ngOnInit(): void {
        this.sourceTenant = this.profileService.getDatastore();
        this.datastoreService.getAllDatastoresUsingGET().subscribe(datastores => {
            this.allDatastores = datastores;
        });
    }

    get targetDatastores(): DatastoreDto[] {
        return this.allDatastores.filter(ds => ds.code !== this.sourceTenant);
    }

    get cartByType(): { type: TransferableType; label: string; lines: CartLine[] }[] {
        return this.types
            .map(type => ({ type, label: this.typeLabel(type), lines: this.cart.filter(line => line.type === type) }))
            .filter(group => group.lines.length > 0);
    }

    codesInCart(type: TransferableType): string[] {
        return this.cart.filter(line => line.type === type).map(line => line.code);
    }

    onSourceTenantChange(): void {
        this.cart = [];
        this.invalidateDryRun();
    }

    onTargetOrStrategyChange(): void {
        this.invalidateDryRun();
    }

    addToCart(refs: TransferObjectRef[]): void {
        for (const ref of refs) {
            if (!this.cart.some(line => line.type === ref.type && line.code === ref.code)) {
                this.cart.push(ref);
            }
        }
        this.invalidateDryRun();
    }

    removeFromCart(line: CartLine): void {
        this.cart = this.cart.filter(existing => !(existing.type === line.type && existing.code === line.code));
        this.invalidateDryRun();
    }

    clearCart(): void {
        this.cart = [];
        this.invalidateDryRun();
    }

    /** Also drops the visible report: leaving a stale one on screen after the cart/target/strategy
     *  changes reads as a current result (e.g. "6 items in cart but the report shows 1"), even
     *  though it's just leftover from a smaller/earlier selection. */
    private invalidateDryRun(): void {
        this.lastDryRunSignature = null;
        this.report = null;
    }

    private signature(): string {
        const sortedRefs = [...this.cart]
            .map(r => `${r.type}:${r.code}`)
            .sort()
            .join(',');
        return `${sortedRefs}|${this.targetTenant}|${this.conflictStrategy}`;
    }

    get canRunPreview(): boolean {
        return this.cart.length > 0 && !!this.targetTenant && !this.working;
    }

    get canTransfer(): boolean {
        return this.report !== null && this.reportIsDryRun && this.lastDryRunSignature === this.signature() && !this.working;
    }

    get dryRunIsStale(): boolean {
        return this.cart.length > 0 && this.lastDryRunSignature !== this.signature();
    }

    runDryRun(): void {
        this.runExportThenImport(true);
    }

    runTransfer(): void {
        this.runExportThenImport(false);
    }

    private runExportThenImport(dryRun: boolean): void {
        if (!this.canRunPreview && dryRun) {
            return;
        }
        this.working = true;
        this.transferService.exportObjects(this.sourceTenant, { objects: this.cart }).subscribe({
            next: manifest => {
                this.transferService
                    .importManifest(this.targetTenant, {
                        manifest,
                        dryRun,
                        conflictStrategy: this.conflictStrategy
                    })
                    .subscribe({
                        next: report => {
                            this.working = false;
                            this.report = report;
                            this.reportIsDryRun = dryRun;
                            if (dryRun) {
                                this.lastDryRunSignature = this.signature();
                            } else {
                                this.lastDryRunSignature = null;
                                this.messageService.add({
                                    severity: report.overallStatus === 'FAILED' ? 'error' : 'success',
                                    summary: this.translate.instant('tenantTransfer.main.transferDoneSummary'),
                                    detail: this.translate.instant('tenantTransfer.main.transferDoneDetail', {status: report.overallStatus})
                                });
                            }
                        },
                        error: () => this.onError()
                    });
            },
            error: () => this.onError()
        });
    }

    private onError(): void {
        this.working = false;
        this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
            detail: this.translate.instant('tenantTransfer.main.errorDetail')
        });
    }
}
